import React, { useState, Suspense, createContext } from 'react';
import { toast } from 'sonner';
import { getBuildingDetail, getDetailFromPoint, updateEtape } from '../projectService';
import MainContainer from '../../../components/containers/MainContainer';
import BuildingDetailView from './components/BuildingDetailView';
import { useSelector } from 'react-redux';
import NLeftPanel from '../../../components/NLeftPanel';
import { useMutation, useQueryClient } from 'react-query';
import { apiService } from '../../../utils/api';

const BuildingModal = React.lazy(() => import('./components/modals/BuildingModal'));

export const SearchContext = createContext<any>(undefined);

function Building() {
  const permission = useSelector((state) => state.auth.pagePermission);
  const { modelId, contract } = useSelector((state) => state.contract);
  const contractId = contract?.id ?? null;
  const [visible, setVisible] = useState(false);
  const [editData, setEditData] = useState<any>(null);
  const [detail, setDetail] = useState<any>({});
  const [search, setSearch] = useState(null);
  const [filter, setFilter] = useState({ has_etape: [true] });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const queryClient = useQueryClient();
  const queryKey = 'building';

  const createBuilding = useMutation({
    mutationFn: (values: any) => {
      return apiService.post(`/api/v1/building/`, {
        ...values,
        model: modelId,
        contract: contractId,
      });
    },
    onSuccess: ({ data }) => {
      toast.success('Položka úspěšně vytvořena');
      queryClient.invalidateQueries('building');
      getDetail(data.id);
      setVisible(false);
      setIsSubmitting(false);
    },
    onError: (err) => {
      console.error(err);
      setIsSubmitting(false);
    },
  });

  const editBuilding = useMutation({
    mutationFn: ({ id, values }: { id: any; values: any }) => {
      return updateEtape(id, values);
    },
    onSuccess: ({ data }, { id }: { id: any }) => {
      queryClient.invalidateQueries('building');
      toast.success('Položka úspěšně editována');
      const index = detail.etape_list.findIndex((ele) => ele.id === id);
      const etapeList = [...detail.etape_list];
      etapeList[index] = data;
      if (index > -1) {
        setDetail({ ...detail, etape_list: etapeList });
      }
      setVisible(false);
    },
  });

  const getDetail = async (id) => {
    try {
      const response = await getBuildingDetail(id);
      setDetail(response.data);
    } catch (err) {
      console.error(err);
    }
  };

  const updateEtapeCallback = async (id, values) => {
    editBuilding.mutate({ id, values } as any);
  };

  const handleSubmit = async (
    id,
    { latitude, longitude, title, building_title, description }: any = {}
  ) => {
    setIsSubmitting(true);

    const dataFromGeoCode = (await getDetailFromPoint(latitude, longitude)).data.results[0];

    if (!dataFromGeoCode) {
      toast.error('Chyba', { description: 'Nenalezeno adresní místo' });
      return;
    }

    const { address_components } = dataFromGeoCode;
    const googleGeoCodeData: any = {};
    address_components.forEach((ele) => {
      ele.types.forEach((_type) => {
        if (!googleGeoCodeData[_type]) {
          googleGeoCodeData[_type] = ele?.long_name ?? '';
        }
      });
    });
    console.log(googleGeoCodeData);
    const req = {
      latitude: latitude.substring(0, 9),
      longitude: longitude.substring(0, 9),
      identity_data: {
        street: `${googleGeoCodeData?.route ?? ''} ${googleGeoCodeData?.street_number ?? ''}`,
        locality: googleGeoCodeData?.locality ?? '',
        sub_locality: googleGeoCodeData?.sublocality ?? '',
        postal_code: googleGeoCodeData?.postal_code ?? '',
        ruian_id: '',
      },
      etape: { title, building_title, description },
    };
    try {
      createBuilding.mutate(req);
    } catch (err) {
      console.error(err);
    }
  };

  const onSearch = async (e) => {
    setSearch(e?.target?.value);
  };

  const onFilter = async (value) => {
    setFilter(value);
  };

  return (
    <SearchContext.Provider
      value={{
        search,
        filter,
        setSearch,
        setFilter,
      }}
    >
      <MainContainer>
        <NLeftPanel
          queryKey={queryKey}
          makeActive={getDetail}
          active={detail.id}
          onSearch={onSearch}
          addItem={() => {
            setEditData(null);
            setVisible(true);
          }}
          onFilter={onFilter}
          defaultFilterValues={{ has_etape: [true] }}
          title="Stavby"
          permission={permission}
          filteredValues={{
            has_etape: [
              { value: true, title: 's etapou' },
              { value: false, title: 'bez etapy' },
            ],
          }}
        />
        {visible && (
          <Suspense fallback={<div>Loading...</div>}>
            <BuildingModal
              submitForm={handleSubmit}
              title={editData ? 'Editovat stavbu' : 'Vytvořit stavbu'}
              visible={visible}
              data={editData}
              formProps={
                editData
                  ? {
                      building_title: editData.etape_list?.[0]?.building_title || '',
                      description: editData.etape_list?.[0]?.description || '',
                      title: editData.etape_list?.[0]?.title || '',
                      latitude: editData.latitude || '',
                      longitude: editData.longitude || '',
                    }
                  : undefined
              }
              onClose={() => {
                setVisible(false);
                setEditData(null);
              }}
              isSubmitting={isSubmitting}
            />
          </Suspense>
        )}
        <BuildingDetailView
          data={detail}
          makeEditable={() => {
            setEditData(detail);
            setVisible(true);
          }}
          permission={permission}
          updateEtape={updateEtapeCallback}
          updateDetail={() => getDetail(detail.id)}
        />
      </MainContainer>
    </SearchContext.Provider>
  );
}

export default Building;
